import { useMemo, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
} from "@fluentui/react-components";
import { ArrowDownloadRegular, DismissRegular } from "@fluentui/react-icons";
import type { IIncentiveRequest } from "@/types/requestTypes";
import { RequestStatusBadge } from "@/components/shared/RequestStatusBadge/RequestStatusBadge";
import { PasswordDisplay } from "@/components/shared/PasswordDisplay/PasswordDisplay";
import { PAGE_SIZE, COMPLETED_STATUSES } from "@/constants/appConstants";
import { createIsLastRowChecker, createLastRowTabHandler, suppressGridTabHandler } from "@/utils/gridKeyboardNavigation";
import styles from "./RequestList.module.scss";

ModuleRegistry.registerModules([AllCommunityModule]);

interface IRequestListProps {
  requests: IIncentiveRequest[];
  onDownload: (requestId: string) => Promise<void>;
  downloadingId: string | null;
  downloadError: string | null;
  onDismissError: () => void;
}

function parseRequestDate(dateStr: string): number {
  // Expected format: "MM/DD/YYYY HH:mm"
  const [datePart, timePart] = dateStr.split(" ");
  if (!datePart || !timePart) {
    return 0;
  }
  const [month, day, year] = datePart.split("/");
  const [hours, minutes] = timePart.split(":");
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
  ).getTime();
}

function PartnerOneIdCellRenderer(params: ICellRendererParams<IIncentiveRequest>): ReactNode {
  return <span className={styles.partnerOneId}>{params.value as string}</span>;
}

function SecondaryTextCellRenderer(params: ICellRendererParams<IIncentiveRequest>): ReactNode {
  return <span className={styles.secondaryText}>{params.value as string}</span>;
}

function StatusCellRenderer(params: ICellRendererParams<IIncentiveRequest>): ReactNode {
  if (!params.data) {
    return null;
  }
  return <RequestStatusBadge status={params.data.status} detail={params.data.statusDetail} />;
}

function PasswordCellRenderer(params: ICellRendererParams<IIncentiveRequest>): ReactNode {
  if (!params.data) {
    return null;
  }
  if (COMPLETED_STATUSES.has(params.data.status) && params.data.workbookPassword) {
    return <PasswordDisplay password={params.data.workbookPassword} />;
  }
  return <span className={styles.emptyCell}>-</span>;
}

interface IDownloadCellRendererContext {
  onDownload: (id: string) => Promise<void>;
  downloadingId: string | null;
  isLastRow: (id: string) => boolean;
}

function DownloadCellRenderer(params: ICellRendererParams<IIncentiveRequest>): ReactNode {
  if (!params.data) {
    return null;
  }

  const { onDownload, downloadingId, isLastRow } = params.context as IDownloadCellRendererContext;

  const hasFile = COMPLETED_STATUSES.has(params.data.status) && params.data.downloadUrl;
  const isLast = isLastRow(params.data.id);

  if (!hasFile) {
    // For the last row without a download, we need a focusable element to escape Tab to pagination
    if (isLast) {
      const handleKeyDown = createLastRowTabHandler(isLast, styles.wrapper);
      return (
        <span 
          className={styles.emptyCell}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          role="gridcell"
          aria-label="No download available"
        >
          -
        </span>
      );
    }
    return <span className={styles.emptyCell}>-</span>;
  }

  const isDownloading = downloadingId === params.data.id;
  const handleKeyDown = createLastRowTabHandler(isLast, styles.wrapper);

  return (
    <button
      type="button"
      className={styles.downloadButton}
      onClick={(): void => {
        if (params.data) {
          void onDownload(params.data.id);
        }
      }}
      onKeyDown={handleKeyDown}
      disabled={isDownloading}
      aria-label={`Download workbook for partner ${params.data.partnerOneId}`}
    >
      <span>Download</span>
      <span className={styles.downloadIcon} aria-hidden="true">
        <ArrowDownloadRegular />
      </span>
    </button>
  );
}

const COLUMN_DEFS: ColDef<IIncentiveRequest>[] = [
  {
    headerName: "PartnerOne ID",
    field: "partnerOneId",
    flex: 12,
    cellRenderer: PartnerOneIdCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Partner name",
    field: "partnerName",
    flex: 16,
    cellRenderer: SecondaryTextCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Requested on",
    field: "requestedOn",
    flex: 14,
    cellRenderer: SecondaryTextCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Status",
    field: "status",
    flex: 26,
    cellRenderer: StatusCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Workbook password",
    field: "workbookPassword",
    flex: 18,
    cellRenderer: PasswordCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Download",
    field: "downloadUrl",
    flex: 14,
    cellRenderer: DownloadCellRenderer,
    sortable: false,
    suppressKeyboardEvent: suppressGridTabHandler,
  },
];

export function RequestList({ requests, onDownload, downloadingId, downloadError, onDismissError }: IRequestListProps): ReactNode {
  const gridRef = useRef<AgGridReact<IIncentiveRequest>>(null);

  // Sort by most recent first
  const sortedRequests = useMemo(
    () =>
      [...requests].sort(
        (a, b) => parseRequestDate(b.requestedOn) - parseRequestDate(a.requestedOn),
      ),
    [requests],
  );

  const defaultColDef = useMemo<ColDef<IIncentiveRequest>>(
    () => ({
      resizable: false,
      filter: false,
      suppressMovable: true,
      wrapText: true,
      autoHeight: true,
    }),
    [],
  );

  const isLastRow = useMemo(
    () => createIsLastRowChecker(gridRef, sortedRequests, PAGE_SIZE),
    [sortedRequests],
  );

  const gridContext = useMemo(
    () => ({ onDownload, downloadingId, isLastRow }),
    [onDownload, downloadingId, isLastRow],
  );

  const getRowId = useCallback((params: { data: IIncentiveRequest }): string => params.data.id, []);

  if (sortedRequests.length === 0) {
    return (
      <div className={styles.emptyState} role="status">
        No requests found.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {downloadError && (
        <div className={styles.downloadErrorWrapper}>
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Download failed</MessageBarTitle>
              {downloadError}
            </MessageBarBody>
            <MessageBarActions
              containerAction={
                <button
                  type="button"
                  className={styles.dismissButton}
                  onClick={onDismissError}
                  aria-label="Dismiss error"
                >
                  <DismissRegular />
                </button>
              }
            />
          </MessageBar>
        </div>
      )}
      <div className={styles.gridWrapper}>
        <div className={styles.grid}>
          <AgGridReact<IIncentiveRequest>
            ref={gridRef}
            rowData={sortedRequests}
            columnDefs={COLUMN_DEFS}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            context={gridContext}
            domLayout="autoHeight"
            pagination
            paginationPageSize={PAGE_SIZE}
            suppressRowHoverHighlight
            enableCellTextSelection
            ensureDomOrder
            headerHeight={46}
          />
        </div>
      </div>
    </div>
  );
}
